import { IonButton, IonButtons, IonCol, IonContent, IonHeader, IonIcon, IonInput, IonItem, IonItemDivider, IonLabel, IonList, IonRow, IonTextarea, IonTitle, IonToggle, IonToolbar, useIonAlert } from '@ionic/react';
import { arrowBack } from 'ionicons/icons';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { IonModalWrapper } from './ClockOffLabourModal.style';

import { unwrapResult } from '@reduxjs/toolkit';
import { useHistory } from 'react-router';
import { getLabourTypes } from '../../common/listOptions';
import { logTransaction } from '../../common/log';
import { getLocationOdomoeter, getLocations, setLocationOdometer } from '../../common/mapsUtil';
import { getClockingId, getJobId, getStateDateAsString, getStateStringAsMoment } from '../../common/util';
import { ClockingDirection, ILabourClockingDetails } from '../../models/dms/clocking.model';
import { IRepairOrder } from '../../models/dms/repairOrder.model';
import { IRepairOrderJob } from '../../models/dms/repairOrderJob.model';
import { UNKNOWN_TECH } from '../../models/dms/technician.model';
import { IThunkError } from '../../models/error.model';
import { IGeolocation } from '../../models/geolocation.model';
import { LogType } from '../../models/log.model';
import { RouteNames } from '../../models/routes.model';
import { useDataSync } from '../../providers/DataSyncProvider';
import { useToast } from '../../providers/ToastProvider';
import store from '../../store';
import { setIsInActionState } from '../../store/appSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setDistanceTrackingUserActivatedStoreAsync } from '../../store/settingsSlice';
import { clockOffAttendanceClockingAsync, clockOffLabourClockingAsync, swapLabourClockingAsync } from '../../store/technicianClockingSlice';
import { DashboardSelectedTab } from '../dashboardFooter/DashboardFooter';
import FormSelectable from '../form/formSelectable/FormSelectable';
import JobCard from '../jobCardList/jobCard/JobCard';
import LabourClockingCardContent from '../labourClockingCard/labourClockingCardContent/LabourClockingCardContent';
import LabourClockingCardHeader from '../labourClockingCard/labourClockingCardHeader/LabourClockingCardHeader';
import FormDatePickerButton from '../form/formDatePickerButton/FormDatePickerButton';

export interface IComponentProps {
  showModal: boolean;
  jobData: IRepairOrderJob;
  repairOrder: IRepairOrder | undefined;
  clockOffAttendance?: boolean;
  attendanceEndDateTime?: Date;
  handleDismiss?: () => void;
}

interface IFormInputs extends ILabourClockingDetails {
  techComment: string;
  isAlert: boolean;
  closeRo: boolean;
  delayCode: string;
  delayComment: string;
  labourType: string;
  workCategory: string;
  isDiagnostic: boolean;
  endDateTime: string;
  distanceLocationsRaw: IGeolocation[];
  distance: number;
}

const ClockOffLabourModal: React.FC<IComponentProps> = function ({ showModal, jobData, repairOrder, clockOffAttendance, attendanceEndDateTime, handleDismiss }) {
  let submitButtonRef: HTMLButtonElement | null = null;
  const [isAlert, setIsAlert] = useState(false);
  const [finishTimeMin, setFinishTimeMin] = useState(moment());
  const [finishTimeMax, setFinishTimeMax] = useState(moment());
  const [distanceRaw, setDistanceRaw] = useState(0);
  const [distanceAccurate, setDistanceAccurate] = useState(0);
  const [distanceLocationsRaw, setDistanceLocationsRaw] = useState([] as IGeolocation[]);
  const labourTypes = getLabourTypes();
  const history = useHistory();
  const {
    state: { requestQueueSyncTimer },
  } = useDataSync();
  const {
    actions: { errorToast },
  } = useToast();

  const [presentAlert] = useIonAlert();

  const { labourClocking } = useAppSelector((state) => state.technicianClocking);
  const { delayCodesSync, delayCodesSyncLoading } = useAppSelector((state) => state.delayCodeSync);
  const { workCategoriesSync, workCategoriesSyncLoading } = useAppSelector((state) => state.workCategorySync);

  const dispatch = useAppDispatch();

  const { control, handleSubmit, setValue, reset, watch } = useForm<IFormInputs>({
    defaultValues: {
      techComment: '',
      isAlert: false,
      closeRo: false,
      delayCode: undefined,
      delayComment: '',
      labourType: 'S',
      workCategory: undefined,
      isDiagnostic: false,
      endDateTime: moment().toISOString(true),
      distance: 0,
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (showModal) {
      setFinishTimeMin(getStateStringAsMoment(labourClocking?.startDateTime));
      setValue('endDateTime', moment().toISOString(true));
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  useEffect(() => {
    if (!repairOrder) {
      handleDismiss && handleDismiss();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairOrder]);

  useEffect(() => {
    const calculateDistance = async () => {
      const { labourClocking } = store.getState().technicianClocking;
      if (showModal && labourClocking?.trackDistance) {
        const distance = await getLocationOdomoeter();
        const locations = await getLocations(false, true);

        setDistanceRaw(distance);
        setDistanceLocationsRaw(locations);

        let accurateDistance = 0;

        try {
          logTransaction(LogType.Info, `Accurate distance calc disabled`);
          accurateDistance = distance;
          logTransaction(LogType.Info, `Estimate distance of ${accurateDistance} found`);
          //logTransaction(LogType.Info, `Calculating accurate distance`);
          //accurateDistance = await getAccurateDistance(locations);
          //logTransaction(LogType.Info, `Accurate distance of ${accurateDistance} found`);
          setDistanceAccurate(accurateDistance);
          setValue('distance', parseFloat((accurateDistance / 1000).toFixed(2)));
        } catch (ex) {
          setValue('distance', parseFloat((distance / 1000).toFixed(2)));
        }
      } else {
        setDistanceRaw(0);
        setDistanceAccurate(0);
        setDistanceLocationsRaw([]);
      }
    };

    calculateDistance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const onSubmit = handleSubmit(async (data) => {
    const { technician } = store.getState().auth;
    const endDateTime = moment(data.endDateTime, moment.ISO_8601);
    const { labourClocking } = store.getState().technicianClocking;

    if (getClockingId(labourClocking) === getJobId(jobData)) {
      // Current clocking and selected job match
      // CLOCK OFF
      await presentAlert({
        header: 'Clock Off?',
        subHeader: '',
        message: labourClocking?.jobData?.repairOrderNumber ? `Are you sure you want to clock off job ${labourClocking.jobData.jobType}/${labourClocking.jobData.jobCode}` : `Are you sure you want to clock off this adhoc job?`,
        buttons: [
          {
            text: 'No',
            role: 'cancel',
            cssClass: 'secondary-button',
          },
          {
            text: 'Yes',
            cssClass: 'secondary-button',
            handler: async () => {
              if (labourClocking?.jobData) {
                const details = {
                  ...data,
                  distanceRaw,
                  distanceAccurate,
                  distanceLocationsRaw,
                } as ILabourClockingDetails;
                await clockOffLabour(technician?.code || UNKNOWN_TECH, endDateTime.toDate(), labourClocking?.jobData, details);
              } else {
                const error = 'Unexpected error clocking off labour. There is no labour data available in the current labour clocking!';
                logTransaction(LogType.Error, error);
                errorToast(error);
              }
            },
          },
        ],
        onDidDismiss: () => {
          dispatch(setIsInActionState(false));
        },
      });
    } else {
      // Current clocking and selected job DO NOT match
      // SWAP JOBS
      const offJob = labourClocking?.jobData.isAdhoc ? 'this adhoc job' : `${labourClocking?.jobData?.jobType}/${labourClocking?.jobData?.jobCode}`;
      const onJob = jobData.isAdhoc ? 'this adhoc job' : `${jobData.jobType}/${jobData.jobCode}`;

      await presentAlert({
        header: 'Swap Jobs?',
        subHeader: '',
        message: `Are you sure you want to clock off ${offJob} and on ${onJob}?`,
        buttons: [
          {
            text: 'No',
            role: 'cancel',
            cssClass: 'secondary-button',
          },
          {
            text: 'Yes',
            cssClass: 'secondary-button',
            handler: async () => {
              if (labourClocking?.jobData) {
                const details = {
                  ...data,
                  distanceRaw,
                  distanceAccurate,
                  distanceLocationsRaw,
                } as ILabourClockingDetails;
                if (repairOrder) {
                  await swapLabour(technician?.code || UNKNOWN_TECH, endDateTime.toDate(), { jobData: labourClocking.jobData, details: details }, { jobData, repairOrder });
                } else {
                  const error = `Unexpected error clocking off labour. Repair order is not defined!`;
                  errorToast(error);
                  logTransaction(LogType.Error, error);
                }
              } else {
                const error = 'Unexpected error clocking off labour. There is no labour data available in the current labour clocking!';
                logTransaction(LogType.Error, error);
                errorToast(error);
              }
            },
          },
        ],
        onDidDismiss: () => {
          dispatch(setIsInActionState(false));
        },
      });
    }

    dispatch(setIsInActionState(true));
  });

  const FormTextArea = (control: any, name: string, maxLength: number, label: string, className?: string) => {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <IonTextarea
              label={label}
              labelPlacement="stacked"
              value={value}
              onIonInput={(e) => {
                onChange(e.detail.value);
              }}
              className={className}
              maxlength={maxLength}
              autoGrow
            />
          );
        }}
      />
    );
  };

  const FormToggle = (control: any, name: string, label: string, handleChange?: (val: boolean) => void) => {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <IonToggle
              color="primarylight"
              labelPlacement="start"
              className="width-100pc"
              onIonChange={(e) => {
                onChange(e.detail.checked);
                handleChange && handleChange(e.detail.checked);
              }}
              checked={value}
              slot="end">
              {label}
            </IonToggle>
          );
        }}
      />
    );
  };

  const clockOffLabour = async (technicianCode: string, endDateTime: Date, jobData: IRepairOrderJob, details: ILabourClockingDetails) => {
    const { lastPosition } = store.getState().location;

    try {
      logTransaction(LogType.Info, `Clock off labour requested - jobData: ${jobData.repairOrderNumber}/${jobData.jobCode}`);
      handleDismiss && handleDismiss();

      const resultActionLabour = await dispatch(
        clockOffLabourClockingAsync({
          techCode: technicianCode,
          endDateTime: getStateDateAsString(endDateTime),
          jobData: jobData,
          details: details,
          position: lastPosition,
        }),
      );
      unwrapResult(resultActionLabour);

      // Stop tracking location
      await dispatch(setDistanceTrackingUserActivatedStoreAsync(false));
      await setLocationOdometer(0);

      if (clockOffAttendance && attendanceEndDateTime) {
        if (moment(attendanceEndDateTime).isBefore(moment(endDateTime))) {
          attendanceEndDateTime = endDateTime;
        }

        const resultActionAttendance = await dispatch(
          clockOffAttendanceClockingAsync({
            techCode: technicianCode,
            endDateTime: getStateDateAsString(attendanceEndDateTime),
            position: lastPosition,
          }),
        );
        unwrapResult(resultActionAttendance);
      }

      requestQueueSyncTimer?.fire();

      if (history.location.pathname !== RouteNames.dashboard) {
        history.push({
          pathname: RouteNames.dashboard,
          search: `tab=${DashboardSelectedTab.ClockingSummary}`,
        });
      }
    } catch (ex) {
      const err = (ex as IThunkError).message;
      logTransaction(LogType.Error, `Failed to clock off labour - jobData: ${jobData.repairOrderNumber}/${jobData.jobCode}. ${err}`);
      errorToast(err);
    }
  };

  const swapLabour = async (technicianCode: string, endDateTime: Date, off: { jobData: IRepairOrderJob; details: ILabourClockingDetails }, on: { jobData: IRepairOrderJob; repairOrder: IRepairOrder }) => {
    const { lastPosition } = store.getState().location;

    try {
      logTransaction(LogType.Info, `Swap labour requested - jobData: ${jobData.repairOrderNumber}/${jobData.jobCode}`);
      handleDismiss && handleDismiss();

      const resultAction = await dispatch(
        swapLabourClockingAsync({
          techCode: technicianCode,
          endDateTime: getStateDateAsString(endDateTime),
          off: off,
          on: on,
          position: lastPosition,
        }),
      );
      unwrapResult(resultAction);

      // Stop tracking location
      await dispatch(setDistanceTrackingUserActivatedStoreAsync(false));

      requestQueueSyncTimer?.fire();
      if (history.location.pathname !== RouteNames.dashboard) {
        history.push({
          pathname: RouteNames.dashboard,
          search: `tab=${DashboardSelectedTab.ClockingSummary}`,
        });
      }
    } catch (ex) {
      const err = (ex as IThunkError).message;
      logTransaction(LogType.Error, `Failed to swap labour - jobData: ${jobData.repairOrderNumber}/${jobData.jobCode}. ${err}`);
      errorToast(err);
    }
  };

  const handleOpenDateModal = (isOpen: boolean) => {
    if (isOpen) {
      setFinishTimeMax(moment());
    }
  };

  return (
    <IonModalWrapper
      className="stacked-modal"
      isOpen={showModal}
      onDidDismiss={() => {
        if (showModal) {
          handleDismiss && handleDismiss();
        }
      }}>
      <IonHeader color="lightest">
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonButton fill="solid" onClick={() => handleDismiss && handleDismiss()}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          {getJobId(jobData) === getClockingId(labourClocking) ? <IonTitle>Clock Off Labour</IonTitle> : <IonTitle>Swap Labour</IonTitle>}
        </IonToolbar>
      </IonHeader>
      <IonContent color="lightest">
        {/* <IonCard className="clocking-card"> */}
        <LabourClockingCardHeader />
        <LabourClockingCardContent />

        {/* <IonCardContent> */}
        <section className="ion-padding-horizontal">
          <IonItemDivider color="warning">
            <IonLabel>Fill in clocking details for {labourClocking?.jobData?.jobCode}</IonLabel>
          </IonItemDivider>

          {labourClocking?.direction === ClockingDirection.On && (
            <form id="clockOffLabourForm" noValidate onSubmit={onSubmit}>
              <button
                type="submit"
                className="ion-hide"
                ref={(button) => {
                  if (!submitButtonRef && button) {
                    submitButtonRef = button;
                  }
                }}>
                Submit
              </button>
              <IonList>
                <IonItem className="ion-no-padding">
                  <IonRow className="width-100pc">
                    <IonCol className="ion-no-padding">{FormTextArea(control, 'techComment', 8192, `Technician ${isAlert ? 'Alert' : 'Comment'}`, 'tech-comment')}</IonCol>
                    <IonCol size="auto">
                      <div className="flex-checkbox">{FormToggle(control, 'isAlert', 'Alert', (val: boolean) => setIsAlert(val))}</div>
                    </IonCol>
                  </IonRow>
                </IonItem>
                <IonItem className="checkbox-pad">{FormToggle(control, 'closeRo', 'Is Repair Order Complete?')}</IonItem>
                <FormSelectable control={control} label="Delay Code" name="delayCode" itemKey="code" itemValue="description" placeholder="Delay Code" items={delayCodesSync?.data?.items || []} itemsLoading={delayCodesSyncLoading} />
                <IonItem className="ion-no-padding">{FormTextArea(control, 'delayComment', 100, 'Delay Comment')}</IonItem>
                <FormSelectable control={control} label="Labour Type" name="labourType" itemKey="code" itemValue="description" placeholder="Labour Type" items={labourTypes || []} />
                <FormSelectable
                  control={control}
                  label="Work Category"
                  name="workCategory"
                  itemKey="workCategory"
                  itemValue="workCategory"
                  placeholder="Work Category"
                  items={workCategoriesSync?.data?.items || []}
                  itemsLoading={workCategoriesSyncLoading}
                />
                <IonItem className="checkbox-pad">{FormToggle(control, 'isDiagnostic', 'Diagnostic')}</IonItem>
                <IonItem className="ion-no-padding" disabled>
                  <IonInput value={watch('distance')} disabled label="Distance Travelled (km)" labelPlacement="stacked" />
                </IonItem>
                <FormDatePickerButton
                  control={control}
                  datetimeId="labourfinishtime"
                  label="Finish Time"
                  placeholder="Finish Time"
                  name="endDateTime"
                  min={finishTimeMin.toISOString(true)}
                  max={finishTimeMax.toISOString(true)}
                  handleModalOpen={(isOpen) => handleOpenDateModal(isOpen)}
                />
              </IonList>
            </form>
          )}
          {/* </IonCardContent> */}
          {/* </IonCard> */}
        </section>
        <JobCard job={jobData} onClick={() => submitButtonRef?.click()} />
      </IonContent>
    </IonModalWrapper>
  );
};

export default ClockOffLabourModal;
